import { useSyncExternalStore } from "react";

// Tiny external store for the series panel's per-instance view:
//   • the "auto-expand series with cine clips" user preference
//   • which series thumbnails are expanded (so the panel can widen)
//   • the instance each viewport shows (so the instance tiles can highlight it)
// Kept outside React context so Thumbnail, Viewport and Viewer can share it
// without re-rendering the whole viewer tree.

const listeners = new Set();
const emit = () => listeners.forEach((l) => l());
const subscribe = (l) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

// ── Preference ──────────────────────────────────────────────────────────────
// Source of truth is the backend user preference (on by default); the viewer
// loads it on open. Until it arrives, the default applies.
let ownAutoExpand = true;
let autoExpandCineSeries = ownAutoExpand;

export const getAutoExpandCineSeries = () => autoExpandCineSeries;
/**
 * `persist: false` applies a value for this page only, keeping the user's own
 * preference — the shared viewer follows the link creator's preference, which
 * must not replace a logged-in user's own.
 */
export const setAutoExpandCineSeries = (value, { persist = true } = {}) => {
  const next = !!value;
  if (persist) ownAutoExpand = next;
  if (next === autoExpandCineSeries) return;
  autoExpandCineSeries = next;
  emit();
};
/** Back to the user's own preference (after a page-only override). */
export const restoreAutoExpandCineSeries = () => setAutoExpandCineSeries(ownAutoExpand, { persist: false });
export const useAutoExpandCineSeries = () =>
  useSyncExternalStore(subscribe, getAutoExpandCineSeries);

// ── Expanded series ─────────────────────────────────────────────────────────
// `choices`: the user's explicit expand/collapse per thumbnail index (wins over
// the preference default). `effective`: what each mounted thumbnail currently
// shows — drives the panel width.
const choices = new Map();
const effective = new Map();

export const getSeriesExpandChoice = (index) => choices.get(index);
export const setSeriesExpandChoice = (index, expanded) => {
  choices.set(index, !!expanded);
  emit();
};
export const reportSeriesExpanded = (index, expanded) => {
  if (expanded) {
    if (effective.get(index) === true) return;
    effective.set(index, true);
  } else {
    if (!effective.has(index)) return;
    effective.delete(index);
  }
  emit();
};
const anyExpanded = () => effective.size > 0;
export const useAnySeriesExpanded = () => useSyncExternalStore(subscribe, anyExpanded);
export const useSeriesExpandChoice = (index) =>
  useSyncExternalStore(subscribe, () => choices.get(index));

/** Forget per-study state (call when a different study is opened). */
export const resetSeriesPanelState = () => {
  choices.clear();
  effective.clear();
  viewportInstances.clear();
  emit();
};

// ── Instance shown per viewport ─────────────────────────────────────────────
// viewportIndex → { thumbnailIndex, instanceIndex }. instanceIndex is the
// series (outer) index — for a series of cine clips, the clip, not the frame.
const viewportInstances = new Map();
export const publishViewportInstance = (viewportIndex, thumbnailIndex, instanceIndex) => {
  const prev = viewportInstances.get(viewportIndex);
  if (prev && prev.thumbnailIndex === thumbnailIndex && prev.instanceIndex === instanceIndex) return;
  viewportInstances.set(viewportIndex, { thumbnailIndex, instanceIndex });
  emit();
};
export const useViewportInstance = (viewportIndex) =>
  useSyncExternalStore(subscribe, () => viewportInstances.get(viewportIndex));
